import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import React from "react";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {useFeedbackTools} from "../../../../components/FeedbackTools";
import {useCheckInCreateAlloggiatiSync, useCheckInCancel} from "../../query";
import CircularProgress from "@mui/material/CircularProgress";
import {ABIT_STATUS, ALLOGGIATI_STATUS} from "../../../../services/bookings/enums";
import * as Sentry from "@sentry/react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";


const CheckInsList = ({booking}) => {
    const {showSuccessNotification, showErrorNotification, askConfirmation} = useFeedbackTools();
    const checkIns = booking.checkIns;
    const checkInCancel = useCheckInCancel({bookingId: booking.id});

    const handleCancel = (checkIn) => {
        askConfirmation(
            {
                title: "Annulla Check-In",
                description: `Annullando il check-in saranno anche eliminate eventuali presenze caricate su Abit. Confermi l'annullamento del check-in?`,
                confirmationText: 'Si',
                cancellationText: 'No',
                onConfirm: async () => {
                    try {
                        await checkInCancel.mutateAsync({checkInId: checkIn.id});
                        showSuccessNotification("Check-In annullato con successo");
                    } catch (e) {
                        showErrorNotification(e);
                    }
                }
            },
        );
    }

    return <Box sx={{mb: 3}}>
        <Divider />
        {
            checkIns.map(c => (
                <React.Fragment key={c.id}>
                    <Grid container alignItems="center" sx={{py: 1, px: 1}} rowSpacing={1}>
                        <Grid item xs={12} md={5}>
                            <Typography variant="body2">
                                Arrivo: {c.getCheckInDateString()}
                            </Typography>
                            <Typography variant="body2">
                                Partenza (attesa): {c.getExpectedCheckOutDateString()}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Typography variant="body2" fontWeight="light">
                                {
                                    c.syncAlloggiati ? "Sinc. Alloggiati abilitata" : "Sinc. Alloggiati disabilitata"
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Typography variant="body2" fontWeight="light">
                                {
                                    c.syncAbit ? "Sinc. Abit abilitata" : "Sinc. Abit disabilitata"
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3} sx={{display: "flex", justifyContent: "end"}}>
                            <Button variant="text" color="error" onClick={() => handleCancel(c)} size="small"
                                    disabled={!booking.isEditable()}>
                                Annulla Check-In
                            </Button>
                        </Grid>
                        {
                            c.syncAlloggiati && <Grid item xs={12}>
                                <AlloggiatiStatus checkIn={c}/>
                            </Grid>
                        }
                        {
                            c.syncAbit && <Grid item xs={12}>
                                <AbitArrivalsStatus checkIn={c}/>
                            </Grid>
                        }
                        {
                            c.syncAbit && <Grid item xs={12}>
                                <AbitDeparturesStatus checkIn={c}/>
                            </Grid>
                        }
                    </Grid>
                    <Divider/>
                </React.Fragment>
            ))
        }
    </Box>
}

const AlloggiatiStatus = ({checkIn}) => {
    const status = checkIn.alloggiatiJobStatus;
    const checkInCreateAlloggiatiSync = useCheckInCreateAlloggiatiSync({bookingId: checkIn.bookingId});
    const {askConfirmation} = useFeedbackTools();

    const uploadAlloggiati = () => {
        askConfirmation(
            {
                title: "Carica schedine Alloggiati",
                description: `Confermi di voler caricare le schedine sul portale Alloggiati?`,
                confirmationText: 'Si',
                cancellationText: 'No',
                onConfirm: async () => {
                    await checkInCreateAlloggiatiSync.mutateAsync({checkInId: checkIn.id});
                }
            },
        );
    }

    if (ALLOGGIATI_STATUS.ready === status) {
        return <Typography variant="body2" fontWeight="light" color="info.main">
            Caricamento non eseguito
        </Typography>
    } else if (ALLOGGIATI_STATUS.scheduled === status || ALLOGGIATI_STATUS.running === status) {
        return <Alert severity="info" variant="outlined">
            <CircularProgress size={26} color="inherit" sx={{mb: 2}}/>
            <Typography variant="subtitle2">
                Invio schedine in corso
            </Typography>
            <Typography variant="caption">
                L'operazione potrebbe richiedere qualche minuto
            </Typography>
        </Alert>
    } else if (ALLOGGIATI_STATUS.completed === status) {
        return <Alert severity="success" variant="outlined">
            Schedine alloggiati caricate correttamente
        </Alert>
    } else if (ALLOGGIATI_STATUS.failed === status) {
        return (
            <Alert severity="error" variant="outlined" action={
                <Button color="inherit" size="small" sx={{alignSelf: "end"}} onClick={uploadAlloggiati}>
                    Carica nuovamente
                </Button>
            }>
                <AlertTitle>Errore caricamento schedine</AlertTitle>
                {checkIn.alloggiatiJobError}
            </Alert>
        );
    } else {
        const msg = `Got an unexpected Allogiati job status (${checkIn.alloggiatiJobStatus}) form the server.`
        console.error(msg)
        Sentry.captureMessage(msg);
        return null
    }
}

const AbitArrivalsStatus = ({checkIn}) => {
    if (checkIn.abitArrivalsStatus === null) {
        return null;
    } else if (checkIn.abitArrivalsStatus === ABIT_STATUS.pending) {
        return <Alert severity="info" variant="outlined">
            Sincronizzazione arrivi su Abit programmata a fine giornata
        </Alert>
    } else if (checkIn.abitArrivalsStatus === ABIT_STATUS.completed) {
        return <Alert severity="success" variant="outlined">
            Arrivi sincronizzati correttamente su Abit
        </Alert>
    } else if (checkIn.abitArrivalsStatus === ABIT_STATUS.failed) {
        return <Alert severity="error" variant="outlined">
            Errore durante la sincronizzazione degli arrivi su Abit
        </Alert>
    } else {
        const msg = `Got an unexpected Abit arrivals status (${checkIn.abitArrivalsStatus}) form the server.`
        console.error(msg)
        Sentry.captureMessage(msg);
        return null
    }
}

const AbitDeparturesStatus = ({checkIn}) => {
    if (checkIn.abitDeparturesStatus === null) {
        return null;
    } else if (checkIn.abitDeparturesStatus === ABIT_STATUS.pending) {
        return <Alert severity="info" variant="outlined">
            Sincronizzazione partenze su Abit programmata a fine giornata
        </Alert>
    } else if (checkIn.abitDeparturesStatus === ABIT_STATUS.completed) {
        return <Alert severity="success" variant="outlined">
            Partenze sincronizzate correttamente su Abit
        </Alert>
    } else if (checkIn.abitDeparturesStatus === ABIT_STATUS.failed) {
        return <Alert severity="error" variant="outlined">
            Errore durante la sincronizzazione delle partenze su Abit
        </Alert>
    } else {
        const msg = `Got an unexpected Abit departures status (${checkIn.abitDeparturesStatus}) form the server.`
        console.error(msg)
        Sentry.captureMessage(msg);
        return null
    }
}

CheckInsList.propTypes = {
    booking: PropTypes.object.isRequired,
}

export default CheckInsList;
